import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Inject,
  OnInit,
  signal,
} from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { TextConstants } from "@shared/constants";
import { CustomFieldsResourceTypeEnum } from "@shared/enums";
import {
  ICustomField,
  IMaterial,
  IProduct,
  IProductPayload,
  ISelectOption,
} from "@shared/interfaces";
import {
  NotificationService,
  AuthenticationService,
  CustomFieldsService,
  MaterialsService,
  ProductsService,
  CommonService,
} from "@shared/services";
import { CommonUtils, CustomFieldsUtils, FormUtils, LocalizationUtils } from "@shared/utils";
import { CustomValidators } from "@shared/validators";

@Component({
  standalone: false,
  selector: "app-add-product-dialog",
  templateUrl: "./add-product-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddProductDialogComponent implements OnInit {
  public formGroup: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl(
      null,
      [CustomValidators.required],
      [CustomValidators.entityAlreadyExists(this.productsService, null)],
    ),
    baseUnit: new UntypedFormControl(null, [CustomValidators.required]),
    hsCode: new UntypedFormControl(null, [
      CustomValidators.digitsAndDots,
      CustomValidators.maxLength(50),
    ]),
    description: new UntypedFormControl(null, [CustomValidators.maxLength(255)]),
    existingMaterial: new UntypedFormControl(null),
  });

  public isLoading = signal(false);

  public readonly systemUnitsOfMeasurementsOptions = computed(() =>
    this.commonService.systemUnitsOfMeasurements()?.map((unit) => ({
      label: LocalizationUtils.getUnitOfMeasurementCategory(unit.type),
      value: unit.id,
    })),
  );

  public visibleCustomFields: ICustomField[] = [];

  private allCustomFields: ICustomField[] = [];

  public materialOptions: ISelectOption[] = [];

  public allMaterials: IMaterial[] = [];

  public readonly translations: any = {
    nameLabel: TextConstants.NAME,
    baseUnitLabel: TextConstants.MAIN_MEASUREMENT_TYPE,
    existingMaterialLabel: TextConstants.MATERIAL,
    hsCodeLabel: TextConstants.HS_CODE,
    hsCodeHint: TextConstants.HS_CODE_HINT,
    descriptionLabel: TextConstants.DESCRIPTION,
  };

  private activeOrganisationId = this.authenticationService.getActiveOrganisationId();

  constructor(
    private dialogRef: MatDialogRef<AddProductDialogComponent>,
    private productsService: ProductsService,
    private notificationService: NotificationService,
    private commonService: CommonService,
    private customFieldService: CustomFieldsService,
    private materialsService: MaterialsService,
    private authenticationService: AuthenticationService,
    @Inject(MAT_DIALOG_DATA)
    private data: {
      product?: IProduct;
      selectedMaterials?: IMaterial[];
    },
  ) {}

  public async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    await Promise.all([
      await this.setMaterialsOptions(),
      (this.allCustomFields = await this.customFieldService.getAll(
        CustomFieldsResourceTypeEnum.PRODUCT,
      )),
    ]);

    this.setUpForm();
  }

  private setUpForm(): void {
    const selectedMaterials = this.data?.selectedMaterials ?? [];

    this.formGroup.controls["name"].setValue(this.data?.product?.name ?? "");
    this.formGroup.controls["baseUnit"].setValue(
      this.systemUnitsOfMeasurementsOptions().find(
        (unit) => unit.value === CommonUtils.getUriId(this.data?.product?.baseUnit ?? ""),
      ),
    );
    this.formGroup.controls["hsCode"].setValue(this.data?.product?.hsCode ?? "");
    this.formGroup.controls["description"].setValue(this.data?.product?.description ?? "");
    this.formGroup.controls["existingMaterial"].setValue(selectedMaterials);
    this.visibleCustomFields = CustomFieldsUtils.getVisible(this.allCustomFields, []);
    CustomFieldsUtils.addToFormGroup(
      this.formGroup,
      this.visibleCustomFields,
      this.data?.product?.customFields ?? [],
    );
    this.isLoading.set(false);
  }

  public async onSubmit(): Promise<void> {
    if (!(await this.formGroup)) {
      FormUtils.findAndMarkInvalidControls(this.formGroup);
      this.notificationService.showError(TextConstants.FILL_REQUIRED_FIELDS);

      return;
    }
    this.isLoading.set(true);

    const baseUnit = `/common/base-units/${this.formGroup.controls["baseUnit"].value.value}`;
    const defaultUnit = baseUnit;
    const allowedMaterials = this.formGroup.controls["existingMaterial"].value.map(
      (material) => `/organisations/${this.activeOrganisationId}/materials/${material.value}`,
    );

    const payload: IProductPayload = {
      baseUnit,
      defaultUnit,
      name: this.formGroup.controls["name"].value.trim(),
      hsCode: this.formGroup.controls["hsCode"].value?.trim() || undefined,
      description: this.formGroup.controls["description"].value?.trim() || undefined,
      allowedMaterials,
    };

    const createdProduct = await this.productsService.createOrUpdate(payload);

    createdProduct.customUnits = [];

    this.notificationService.showSuccess($localize`:@@shared.productcreated:Product created`);

    this.isLoading.set(false);

    this.onClose(true, createdProduct);
  }

  private async setMaterialsOptions(): Promise<void> {
    this.allMaterials = await this.materialsService.getAll();

    this.materialOptions = this.allMaterials.map((material) => ({
      label: material.name,
      value: material.id,
    }));
  }

  public onClose(hasSaved: boolean = false, createdProduct?: IProduct): void {
    this.dialogRef.close({ hasSaved, createdProduct });
  }
}
